import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';

import { useParams } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { makeStyles } from '@material-ui/core/styles';
import Page from '../../components/Page';

import { Box, Button, Card, CardContent, Typography, Grid } from '@material-ui/core';

import PageHeader from '../../components/PageHeader';
import Spacer from '../../components/Spacer';
import UnlockWallet from '../../components/UnlockWallet';
import Harvest from './components/Harvest';
import Stake from './components/Stake';
import useBank from '../../hooks/useBank';
import useStatsForPool from '../../hooks/useStatsForPool';
import useRedeem from '../../hooks/useRedeem';
import { Bank as BankEntity } from '../../tomb-finance';
import useTombFinance from '../../hooks/useTombFinance';
import CardBG from '../../assets/img/CardBackground.png'
import CardBGHome2 from '../../assets/img/CardBackground-Home2.png'
import CardBGHome3 from '../../assets/img/CardBackground-3.png'
import CardPyramid from '../../components/CardPyramid';
import GenesisCountdown from './components/GenesisCountdown';
import moment from 'moment';
import ProgressCountdown from '../Cemetery/ProgressCountdown';
import useElysiumTokenPerSecond from '../../hooks/useElysiumTokenPerSecond';
import ShimmerLoader from '../../components/ShimmerLoader';

type CSSProperties = React.CSSProperties & {
  [key: `--${string}`]: string | number;
};

const marqueeContainerStyle: CSSProperties = {
  '--pause-on-hover': 'running',
  '--pause-on-click': 'running',
  '--width': '100%',
  '--transform': 'none',
  overflow: 'hidden',
  position: 'relative'
};

const overlayStyle: CSSProperties = {
  '--gradient-color': 'hsl(var(--background))',
  '--gradient-width': '50px',
  '--darkreader-bg--gradient-color': 'var(--darkreader-background-ffedd6, #4c2a00)',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 2
};

const marqueeStyle: CSSProperties = {
  '--play': 'running',
  '--direction': 'reverse',
  '--duration': '30s',
  '--delay': '0s',
  '--iteration-count': 'infinite',
  '--min-width': 'auto',
  display: 'flex',
  animation: 'scroll 30s linear infinite',
  whiteSpace: 'nowrap'
};

const childStyle: CSSProperties = {
  '--transform': 'none'
};

interface BankRouteParams {
  bankId: string;
}

const useStyles = makeStyles((theme) => ({
  gridItem: {
    height: '100%',
    [theme.breakpoints.up('md')]: {
      height: '90px',
    },
  },
}));

const getNextWednesday8PMUTC = () => {
  const now = moment.utc();
  const skipWednesday = moment.unix(1741215600).utc(); // The Wednesday we want to skip
  
  // Get next Wednesday
  let nextWednesday = now.clone().day(3); // 3 for Wednesday
  nextWednesday.set({ hour: 20, minute: 0, second: 0, millisecond: 0 });
  
  // If we've already passed this Wednesday 8 PM or if this is the Wednesday we want to skip,
  // get next week's Wednesday
  if (nextWednesday.isSameOrBefore(now) || nextWednesday.isSame(skipWednesday, 'day')) {
    nextWednesday.add(1, 'week');
  }
  
  // If this is still the Wednesday we want to skip, add another week
  if (nextWednesday.isSame(skipWednesday, 'day')) {
    nextWednesday.add(1, 'week');
  }
  
  return nextWednesday.toDate();
};

const formatNumberWithSpaces = (number: string | undefined) => {
  if (!number) return '0';
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const Bank2: React.FC = () => {
  useEffect(() => window.scrollTo(0, 0));
  const classes = useStyles();
  const { bankId } = useParams<BankRouteParams>();
  const bank = useBank(bankId);

  const { address: account } = useAccount();
  const { onRedeem } = useRedeem(bank);
  const statsOnPool = useStatsForPool(bank, 2);
  const tokenPerSecond = useElysiumTokenPerSecond(bank);
  const tombFinance = useTombFinance();
  const [poolEndTime, setPoolEndTime] = useState<Date | null>(null);
  const [isPoolStarted, setIsPoolStarted] = useState<boolean>(false);
  const [deadline, setDeadline] = useState<Date | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);

  const startDragging = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    const pageX = 'touches' in e ? e.touches[0].pageX : (e as React.MouseEvent).pageX;
    setStartX(pageX);
    setPrevTranslate(currentTranslate);

    // Stop animation while dragging
    const marquee = containerRef.current?.querySelector('.rfm-marquee') as HTMLElement;
    if (marquee) {
      marquee.style.animation = 'none';
      marquee.style.transform = `translateX(${currentTranslate}px)`;
    }
  };

  const handleDrag = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const pageX = 'touches' in e ? e.touches[0].pageX : (e as React.MouseEvent).pageX;
    const currentX = pageX;
    const diff = currentX - startX;
    const newTranslate = prevTranslate + diff;
    
    setCurrentTranslate(newTranslate);
    
    // Update transform while dragging
    const marquee = containerRef.current?.querySelector('.rfm-marquee') as HTMLElement;
    if (marquee) {
      marquee.style.transform = `translateX(${newTranslate}px)`;
    }
  };

  const stopDragging = () => {
    setIsDragging(false);

    // Resume animation
    const marquee = containerRef.current?.querySelector('.rfm-marquee') as HTMLElement;
    if (marquee) {
      marquee.style.transform = '';
      marquee.style.animation = 'scroll 20s linear infinite';
    }
  };

  useEffect(() => {
    const fetchPoolTimes = async () => {
      const { HogGenesisRewardPool } = tombFinance.contracts;
      const startTime = await HogGenesisRewardPool.poolStartTime();
      const endTime = await HogGenesisRewardPool.poolEndTime();
      const currentTime = Date.now();
      const poolStartTime = new Date(startTime.toNumber() * 1000);
      
      if (currentTime < poolStartTime.getTime()) {
        setIsPoolStarted(false);
        setDeadline(poolStartTime);
      } else {
        setIsPoolStarted(true);
        setDeadline(new Date(endTime.toNumber() * 1000));
      }
    };
    fetchPoolTimes();
  }, [tombFinance]);
  
  return account && bank ? (
    <Page>
     <div className="theme-dark bg-background text-foreground">
  <div className="container flex flex-col md:flex-row items-center gap-3 mx-auto justify-center py-4 overflow-hidden">
    <div className="whitespace-wrap opacity-60 md:max-w-32">Stats:</div>
    <div 
      className="rfm-marquee-container relative overflow-hidden w-full cursor-grab active:cursor-grabbing"
      ref={containerRef}
      onMouseDown={startDragging}
      onTouchStart={startDragging}
      onMouseMove={handleDrag}
      onTouchMove={handleDrag}
      onMouseUp={stopDragging}
      onTouchEnd={stopDragging}
      onMouseLeave={stopDragging}
    >
      <div 
        className="rfm-marquee flex gap-4"
        style={{
          animation: isDragging ? 'none' : 'scroll 20s linear infinite',
          transform: isDragging ? `translateX(${currentTranslate}px)` : '',
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
          willChange: 'transform'
        }}
      >
        {/* Two sets of identical cards to ensure smooth looping */}
        {[...Array(2)].map((_, i) => (
          <div key={i} className="flex gap-4 min-w-max">
            {/* APR Card */}
            <div className="rfm-child">
              <div className="min-w-[200px] p-4 bg-card rounded-lg shadow-lg flex justify-between items-center">
                <span style={{ fontWeight: 'bold' }}>APR:</span>
                {bank.closedForStaking ? (
                  <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>0.00%</span>
                ) : statsOnPool ? (
                  <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>{statsOnPool.yearlyAPR}%</span>
                ) : (
                  <ShimmerLoader width="w-16" height="h-4" />
                )}
              </div>
            </div>

            {/* Daily APR Card */}
            <div className="rfm-child">
              <div className="min-w-[200px] p-4 bg-card rounded-lg shadow-lg flex justify-between items-center">
                <span style={{ fontWeight: 'bold' }}>Daily APR:</span>
                {bank.closedForStaking ? (
                  <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>0.00%</span>
                ) : statsOnPool ? (
                  <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>{statsOnPool.dailyAPR}%</span>
                ) : (
                  <ShimmerLoader width="w-16" height="h-4" />
                )}
              </div>
            </div>

            {/* GHOG Per Sec Card */}
            <div className="rfm-child">
              <div className="min-w-[200px] p-4 bg-card rounded-lg shadow-lg flex justify-between items-center">
                <span style={{ fontWeight: 'bold' }}>GHOG Per Sec:</span>
                {tokenPerSecond ? (
                  <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>{tokenPerSecond.toString()}</span>
                ) : (
                  <ShimmerLoader width="w-16" height="h-4" />
                )}
              </div>
            </div>

            {/* Next GHOG Per Sec Decision Card */}
            <div className="rfm-child">
              <div className="min-w-[200px] p-4 bg-card rounded-lg shadow-lg flex justify-between items-center">
                <span style={{ fontWeight: 'bold' }}>Next GHOG Per Sec Decision:</span>
                {getNextWednesday8PMUTC() ? (
                  <span style={{ marginLeft: '8px' }}>
                    <ProgressCountdown 
                      base={moment().toDate()} 
                      hideBar={true} 
                      deadline={getNextWednesday8PMUTC()} 
                      description="Next GHOG Per Sec Decision" 
                    />
                  </span>
                ) : (
                  <ShimmerLoader width="w-24" height="h-4" />
                )}
              </div>
            </div>

            {/* TVL Card */}
            <div className="rfm-child">
              <div className="min-w-[200px] p-4 bg-card rounded-lg shadow-lg flex justify-between items-center">
                <span style={{ fontWeight: 'bold' }}>Total Value Locked:</span>
                {statsOnPool ? (
                  <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>
                    ${formatNumberWithSpaces(statsOnPool.TVL)}
                  </span>
                ) : (
                  <ShimmerLoader width="w-24" height="h-4" />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>

  <style>
    {`
      .animate-scroll {
        animation: scroll 20s linear infinite;
        display: flex;
        width: max-content; /* Ensure continuous flow */
      }

      .rfm-marquee-container {
        mask-image: linear-gradient(
          to right,
          transparent,
          black 15%,
          black 85%,
          transparent
        );
        touch-action: pan-x;
      }

      @keyframes scroll {
        0% {
          transform: translateX(0);
        }
        100% {
          transform: translateX(-50%);
        }
      }

      .rfm-marquee {
        white-space: nowrap;
        will-change: transform;
      }

      .rfm-child {
        display: inline-block;
      }
    `}
  </style>
</div>

      <main className="theme-light bg-background text-foreground flex flex-col">
        <section className="transition-colors duration-700 bg-background text-foreground py-20 relative overflow-hidden theme-dark">
          <div className="absolute inset-0">
            <div className="relative container mx-auto">
              <div className="absolute w-full top-0 opacity-50 [mask-image:linear-gradient(to_right,transparent,black_50%,black_50%,transparent)]">
                <svg
                  className="-scale-y-100 w-full h-40"
                  width="100%"
                  height="200"
                  viewBox="0 0 100 200"
                  preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id="grad-light" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop stopColor="#102D3C"></stop>
                      <stop offset="0.161465" stopColor="#214E81"></stop>
                      <stop offset="0.363625" stopColor="#506179"></stop>
                      <stop offset="0.651499" stopColor="#ED5409"></stop>
                      <stop offset="1" stopColor="#FFCB67"></stop>
                    </linearGradient>
                    <linearGradient id="grad-dark" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop stopColor="#FFCB67"></stop>
                      <stop offset="0.161465" stopColor="#ED5409"></stop>
                      <stop offset="0.363625" stopColor="#506179"></stop>
                      <stop offset="0.651499" stopColor="#214E81"></stop>
                      <stop offset="1" stopColor="#102D3C"></stop>
                    </linearGradient>
                    <mask id="rectanglesMask">
                      <line x1="0" y1="0" x2="100" y2="0" stroke="white" strokeWidth="1"></line>
                      <line x1="0" y1="10" x2="100" y2="10" stroke="white" strokeWidth="1"></line>
                      <line x1="0" y1="20" x2="100" y2="20" stroke="white" strokeWidth="1.5"></line>
                      <line x1="0" y1="30" x2="100" y2="30" stroke="white" strokeWidth="2"></line>
                      <line x1="0" y1="40" x2="100" y2="40" stroke="white" strokeWidth="2.5"></line>
                      <line x1="0" y1="50" x2="100" y2="50" stroke="white" strokeWidth="3"></line>
                      <line x1="0" y1="60" x2="100" y2="60" stroke="white" strokeWidth="3.5"></line>
                      <line x1="0" y1="70" x2="100" y2="70" stroke="white" strokeWidth="4"></line>
                      <line x1="0" y1="80" x2="100" y2="80" stroke="white" strokeWidth="4.5"></line>
                      <line x1="0" y1="90" x2="100" y2="90" stroke="white" strokeWidth="5"></line>
                      <line x1="0" y1="100" x2="100" y2="100" stroke="white" strokeWidth="5.5"></line>
                      <line x1="0" y1="110" x2="100" y2="110" stroke="white" strokeWidth="6"></line>
                      <line x1="0" y1="120" x2="100" y2="120" stroke="white" strokeWidth="6.5"></line>
                      <line x1="0" y1="130" x2="100" y2="130" stroke="white" strokeWidth="7"></line>
                      <line x1="0" y1="140" x2="100" y2="140" stroke="white" strokeWidth="7.5"></line>
                      <line x1="0" y1="150" x2="100" y2="150" stroke="white" strokeWidth="8"></line>
                      <line x1="0" y1="160" x2="100" y2="160" stroke="white" strokeWidth="8.5"></line>
                      <line x1="0" y1="170" x2="100" y2="170" stroke="white" strokeWidth="9"></line>
                      <line x1="0" y1="180" x2="100" y2="180" stroke="white" strokeWidth="9.5"></line>
                      <line x1="0" y1="190" x2="100" y2="190" stroke="white" strokeWidth="10"></line>
                      <line x1="0" y1="200" x2="100" y2="200" stroke="white" strokeWidth="10.5"></line>
                    </mask>
                  </defs>
                  <rect
                    width="100%"
                    height="100%"
                    className="fill-[url(#grad-light)] theme-dark:fill-[url(#grad-dark)]"
                    mask="url(#rectanglesMask)"
                  ></rect>
                </svg>
              </div>
            </div>
          </div>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute inset-0 opacity-[0.15] transition-opacity duration-700"
              style={{
                backgroundImage: 'radial-gradient(circle at center, rgb(255 255 255 / 0.4) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
            ></div>
            <div
              className="absolute left-[15%] top-[50%] w-[40%] opacity-30 transition-[filter,transform,background] duration-700"
              style={{
                aspectRatio: '2/1',
                background: 'radial-gradient(#FF8A3D 0%, transparent 90%)',
                filter: 'blur(120px)',
                transform: 'rotate(45deg)',
              }}
            ></div>
            <div
              className="absolute right-[15%] top-[30%] w-[40%] opacity-30 transition-[filter,transform,background] duration-700"
              style={{
                aspectRatio: '2/1',
                background: 'radial-gradient(#3D76FF 0%, transparent 90%)',
                filter: 'blur(120px)',
                transform: 'rotate(-45deg)',
              }}
            ></div>
          </div>
      <div className="flex flex-col items-center gap-y-8 relative">
        <div className="container mx-auto flex flex-col gap-y-4 items-center">

          <h2 className="font-urbanist text-m-h2 md:text-t-h2 lg:text-d-h2 font-medium text-center max-w-[14ch] leading-none md:leading-tight">
            {bank.depositTokenName} Farm
          </h2>
          <p className="font-urbanist text-m-body-large md:text-t-body-large lg:text-d-body-large text-foreground font-normal leading-relaxed max-w-[40rem]" style={{ textAlign: "center" }}>
            Stake {bank.depositTokenName} and earn {bank.earnTokenName}
          </p>
          <div className="container mx-auto" style={{ marginTop: "20px" }}>
            <StyledBoardroom>
              <StyledCardsWrapper>
                <StyledCardWrapper>
                  <Harvest bank={bank} />
                </StyledCardWrapper>
                <Spacer />
                <StyledCardWrapper>{<Stake bank={bank} />}</StyledCardWrapper>
              </StyledCardsWrapper>
              <div style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "center"  // Centers the button horizontally
              }}> 
              <button onClick={onRedeem} className='tracking-normal inline-flex whitespace-nowrap backdrop-blur items-center justify-center gap-x-1 py-2 px-6 rounded-full font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 [&>svg]:stroke-[3px] text-button-foreground bg-button/[0.16] hover:bg-button/[0.24] transition focus-visible:ring-button text-body'>
                  Claim & Withdraw
                </button>
              </div>
              <Spacer size="lg" />
            </StyledBoardroom>
          </div>
        </div>
      </div>
      </section>
      </main>
    </Page>
  ) : !bank ? (
    <BankNotFound />
  ) : (
    <UnlockWallet />
  );
};
const BankNotFound = () => {
  return (
    <Center>
      <PageHeader icon="🏚" title="Not Found" subtitle="You've reached a non-existent page!" />
    </Center>
  );
};

const StyledLink = styled.a`
  font-weight: 700;
  text-decoration: none;
  color: ${(props) => props.theme.color.primary.main};
`;

const StyledBoardroom = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StyledCardsWrapper = styled.div`
  display: flex;
  width: 600px;
  @media (max-width: 768px) {
    width: 100%;
    flex-flow: column nowrap;
    align-items: center;
  }
`;

const StyledCardWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  @media (max-width: 768px) {
    width: 80%;
  }
`;

const Center = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;


export default Bank2;
